// src/components/auth/ForgotPasswordModal.tsx
import React, { useState, useEffect } from 'react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../config/firebase';
import '../../styles/layout/Modal.css';

interface ForgotPasswordModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({ isOpen, onClose }) => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        const handleEscKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscKey);
        }

        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [isOpen, onClose]);

    // Reset states when modal opens/closes
    useEffect(() => {
        if (!isOpen) {
            setEmail('');
            setError('');
            setSuccess(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await sendPasswordResetEmail(auth, email);
            setSuccess(true);
        } catch (error: any) {
            if (error.code === 'auth/user-not-found') {
                setError('Selle e-posti aadressiga kasutajat ei leitud');
            } else if (error.code === 'auth/invalid-email') {
                setError('Vigane e-posti aadress');
            } else {
                setError('Parooli lähtestamine ebaõnnestus. Proovi hiljem uuesti.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="vm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="vm-reset-title"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="vm-modal__panel">
                <button
                    type="button"
                    className="vm-modal__close"
                    onClick={onClose}
                    aria-label="Sulge"
                >
                    ×
                </button>

                <div className="vm-modal__head">
                    <p className="vm-modal__eyebrow">Ligipääs</p>
                    <h2 className="vm-modal__title" id="vm-reset-title">Unustasid parooli?</h2>
                    {!success && (
                        <p className="vm-modal__sub">
                            Sisesta oma e-posti aadress ja saadame sulle lähtestamise lingi.
                        </p>
                    )}
                </div>

                {success ? (
                    <>
                        <p className="vm-alert vm-alert--ok" role="status">
                            Parooli lähtestamise link on saadetud. Vaata oma e-posti ja järgi
                            juhiseid.
                        </p>
                        <div className="vm-actions">
                            <button type="button" className="vm-submit" onClick={onClose}>
                                Sulge
                            </button>
                        </div>
                    </>
                ) : (
                    <form onSubmit={handleResetPassword} className="vm-form">
                        <div className="vm-field">
                            <label className="vm-field__label" htmlFor="vm-reset-email">
                                E-posti aadress
                            </label>
                            <input
                                id="vm-reset-email"
                                type="email"
                                placeholder="sinu@email.ee"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="vm-input"
                                disabled={loading}
                                autoComplete="email"
                                autoFocus
                            />
                        </div>

                        {error && (
                            <p className="vm-alert" role="alert">
                                {error}
                            </p>
                        )}

                        <button
                            type="submit"
                            className="vm-submit"
                            disabled={loading || !email}
                        >
                            {loading ? <><span className="vm-spinner" />Saadan…</> : 'Saada lähtestamise link'}
                        </button>
                        <button
                            type="button"
                            className="vm-linkbtn"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Tühista
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};
